/* eslint-disable react/prop-types */
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import classNames from 'classnames';
import FalconCardHeader from 'components/common/FalconCardHeader';
import LeafletMap from './LeafletMap';
import { toPng } from 'html-to-image';
import cloudDownload from 'assets/img/icons/cloud-download.svg';
import { isEmpty } from 'lodash';
import Model from './Model';
import { FiInfo } from 'react-icons/fi';

const MultiFilterMap = ({
  data,
  coordinates,
  legendData,
  type,
  info,
  variant,
  checkbox_filters,
  label
}) => {
  // console.log(data, 'Multi filter map data');
  const targetElementRef = useRef(null);

  // Sort years in descending order
  const sortYears = useCallback(years => {
    if (!years || !Array.isArray(years)) return [];
    return [...years].sort((a, b) => b - a);
  }, []);

  // Get sorted years from data
  const getSortedYears = useCallback(() => {
    if (!data) return [];
    return sortYears(Object.keys(data));
  }, [data, sortYears]);

  const sortedYears = useMemo(() => getSortedYears(), [getSortedYears]);

  // Initialize state with safe defaults
  const [selectedYear, setSelectedYear] = useState('');
  const [showModal, setShowModal] = useState(false);
  // Single-selection filter state
  const [selectedFilters, setSelectedFilters] = useState([]);

  // Initialize selectedYear and selectedFilters when sortedYears or data changes
  useEffect(() => {
    if (sortedYears && sortedYears.length > 0) {
      const defaultYear = sortedYears[0];
      setSelectedYear(defaultYear);
      // Set default filter for the default year
      if (data && data[defaultYear]) {
        const availableFilters = Object.keys(data[defaultYear]);
        if (availableFilters.length > 0) {
          setSelectedFilters([availableFilters[0]]);
        } else {
          setSelectedFilters([]);
        }
      } else {
        setSelectedFilters([]);
      }
    }
  }, [sortedYears, data]);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const handleYearChange = useCallback(event => {
    const newYear = event.target.value;
    setSelectedYear(newYear);
  }, []);

  // Handle image download
  const onButtonClick = useCallback(async () => {
    if (!targetElementRef.current) return;

    const title = label || 'Map';
    const currentDate = new Date();
    const timestamp = currentDate
      .toISOString()
      .replace(/:/g, '-')
      .replace(/\..+/, '');
    const filename = `${title}_${timestamp}.png`;

    // Create timestamp element
    const timestampElement = document.createElement('div');
    timestampElement.textContent = `Downloaded on: ${currentDate.toLocaleString()}`;
    timestampElement.style.position = 'absolute';
    timestampElement.style.bottom = '2px';
    timestampElement.style.right = '10px';
    timestampElement.style.fontSize = '10px';
    timestampElement.style.color = '#666';
    targetElementRef.current.appendChild(timestampElement);

    try {
      const dataUrl = await toPng(targetElementRef.current);
      targetElementRef.current.removeChild(timestampElement);

      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Error generating image:', err);
      if (targetElementRef.current.contains(timestampElement)) {
        targetElementRef.current.removeChild(timestampElement);
      }
    }
  }, [label]);

  // Get available filters for the selected year
  const getAvailableFilters = useCallback(() => {
    if (!selectedYear || !data || !data[selectedYear]) return [];
    return Object.keys(data[selectedYear]);
  }, [selectedYear, data]);

  // Update selectedFilters if selectedYear changes and current filter is not available
  useEffect(() => {
    const availableFilters = getAvailableFilters();
    if (availableFilters.length > 0) {
      if (
        !selectedFilters.length ||
        !availableFilters.includes(selectedFilters[0])
      ) {
        setSelectedFilters([availableFilters[0]]);
      }
    } else {
      setSelectedFilters([]);
    }
    // eslint-disable-next-line
  }, [selectedYear, data]);

  // Render radio filters for card header
  const renderRadioFilters = useMemo(() => {
    if (!checkbox_filters || checkbox_filters.length === 0) return null;
    const availableFilters = getAvailableFilters();
    return (
      <div className="d-flex align-items-center gap-3">
        {checkbox_filters.map((filter, index) => {
          const isAvailable = availableFilters.includes(filter);
          const isChecked = selectedFilters[0] === filter && isAvailable;
          return (
            <div key={index} className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id={`filter-${index}`}
                name="single-filter"
                value={filter}
                checked={isChecked}
                onChange={e => {
                  if (e.target.checked) setSelectedFilters([filter]);
                }}
                disabled={!isAvailable}
              />
              <label
                className="form-check-label small"
                htmlFor={`filter-${index}`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </label>
            </div>
          );
        })}
      </div>
    );
  }, [checkbox_filters, selectedFilters, getAvailableFilters]);

  const getMapData = useMemo(() => {
    if (!selectedYear || !data || !data[selectedYear]) {
      return [];
    }
    if (!selectedFilters.length) {
      return [];
    }
    const yearData = data[selectedYear];
    const filter = selectedFilters[0];
    if (yearData[filter] && yearData[filter].url) {
      return [
        {
          ...yearData[filter],
          filterName: filter,
          label: filter.charAt(0).toUpperCase() + filter.slice(1)
        }
      ];
    }
    return [];
  }, [selectedYear, data, selectedFilters]);

  // Year selector and filter controls UI
  const endElement = useMemo(
    () => (
      <div className="d-flex flex-column gap-2">
        {/* Year selector and download button row */}
        <Row className="g-1 align-items-center justify-content-end">
          {sortedYears && sortedYears.length > 1 && (
            <Col className="col-auto">
              <Form.Select
                className="ps-2 pe-5 py-0"
                value={selectedYear}
                name="year"
                onChange={handleYearChange}
              >
                {sortedYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Select>
            </Col>
          )}

          <Col className="text-center p-0 d-flex align-items-center">
            <span className="m-auto px-2"></span>
          </Col>

          <Col className="col-auto">
            <Button
              variant="light"
              size="sm"
              className="border-300 me-1 text-600"
              onClick={onButtonClick}
              disabled={getMapData.length === 0}
            >
              <img
                src={cloudDownload}
                alt="Download"
                width={15}
                height={'auto'}
              />
            </Button>
          </Col>
        </Row>
      </div>
    ),
    [
      renderRadioFilters,
      sortedYears,
      selectedYear,
      handleYearChange,
      onButtonClick,
      getMapData.length
    ]
  );

  // console.log(getMapData, 'getMapData');
  return (
    <Card
      ref={targetElementRef}
      className={classNames('h-100', 'overflow-hidden')}
    >
      <FalconCardHeader
        title={label}
        titleTag="h5"
        light={true}
        desc={
          data && data[sortedYears[0]]
            ? `${label} visualization for ${selectedYear}`
            : ''
        }
        radioFilter={renderRadioFilters}
        endEl={endElement}
        infoBtn={!isEmpty(info) && <FiInfo onClick={handleShowModal} />}
      />

      <Card.Body className="p-0">
        {getMapData.length > 0 ? (
          <LeafletMap
            data={{
              coordinates: coordinates,
              mapData: getMapData[0],
              legendData: legendData,
              type: type,
              label: label,
              selectedYear: selectedYear
            }}
            className="h-100 bg-white"
            style={{ minHeight: '410px' }}
          />
        ) : (
          <div
            className="d-flex align-items-center justify-content-center bg-light h-100"
            style={{ minHeight: '410px' }}
          >
            <p className="text-muted">
              No map data available for this selection
            </p>
          </div>
        )}
      </Card.Body>

      <Card.Footer className="bg-white py-2 card-footer-style">
        <h5 className="infrence-info">
          {getMapData.length > 0
            ? `Showing data for ${selectedYear}`
            : 'No data available'}
        </h5>
      </Card.Footer>

      <Model
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        description={info || {}}
        title={label}
      />
    </Card>
  );
};

export default MultiFilterMap;
